import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grow from "@mui/material/Grow";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { LazyIcon } from "../../components/LazyIcon";

type Props = {
  disabled?: boolean;
  options: { label: string; disabled?: boolean; onClick: () => void }[];
  startIcon?: React.ReactNode;
  color?: "inherit" | "primary" | "secondary" | "success" | "error" | "info" | "warning";
  size?: "small" | "medium" | "large";
  variant?: "text" | "outlined" | "contained";
  defaultSelectedIndex?: number;
};

export const SplitButton: React.FC<Props> = ({
  disabled = false,
  options,
  startIcon,
  color = "primary",
  size = "medium",
  variant = "contained",
  defaultSelectedIndex = 0,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = React.useState(defaultSelectedIndex);

  // If there's only one option, render a simple button instead of a split button
  if (options.length === 1) {
    return (
      <Button
        disabled={disabled || options[0].disabled}
        color={color}
        variant={variant}
        startIcon={startIcon}
        onClick={options[0].onClick}
        size={size}
      >
        {options[0].label}
      </Button>
    );
  }

  const handleClick = () => {
    options[selectedIndex].onClick();
  };

  const handleMenuItemClick = (_event: React.MouseEvent<HTMLElement>, index: number) => {
    setSelectedIndex(index);
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  return (
    <React.Fragment>
      <ButtonGroup
        disableElevation
        disabled={disabled}
        color={color}
        variant={variant}
        ref={anchorRef}
        aria-label={t('widgets.splitButton.ariaLabel')}
        size={size}
      >
        <Button startIcon={startIcon} onClick={handleClick}>
          {options[selectedIndex].label}
        </Button>
        <Button
          data-testid="split_button"
          size="small"
          color={color}
          aria-controls={open ? "split-button-menu" : undefined}
          aria-expanded={open ? "true" : undefined}
          aria-label={t('widgets.splitButton.selectStrategyAriaLabel')}
          aria-haspopup="menu"
          onClick={handleToggle}
        >
          <LazyIcon name="ArrowDropDownIcon" />
        </Button>
      </ButtonGroup>
      <Popper
        sx={{
          zIndex: 9999,
        }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom" ? "center top" : "center bottom",
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="split-button-menu" autoFocusItem>
                  {options.map((option, index) => (
                    <MenuItem
                      key={option.label}
                      selected={index === selectedIndex}
                      disabled={option.disabled}
                      onClick={(event) => handleMenuItemClick(event, index)}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </React.Fragment>
  );
};
